import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Row, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { JobTargetGroupsService } from "./service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { useTranslation } from "react-i18next";
import FormErrorSummary from "../../shared/components/FormErrorSummary";

const schema = yup.object().shape({
  title: yup.string().required('formErrors.titleRequired')
});

interface JobTargetGroupState {
  title: string;
}

const JobTargetGroupsForm: React.FC = () => {
  const { t } = useTranslation(); // Initialize translation hook
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [jobTargetGroupData, setJobTargetGroupData] = useState<JobTargetGroupState | null>(null);
  const navigate = useNavigate();
  const { control, handleSubmit, reset, formState: { errors } } = useForm<JobTargetGroupState>({
    resolver: yupResolver(schema),
    defaultValues: {
      title: ""
    }
  });

  useEffect(() => {
    if (isEdit) {
      const fetchJobTargetGroup = async () => {
        try {
          const response = await JobTargetGroupsService.getSingle(+id!);
          const jobTargetGroup = response;
          setJobTargetGroupData({
            title: jobTargetGroup.title
          });
        } catch (error) {
          ErrorToast(error);
        }
      };

      fetchJobTargetGroup();
    }
  }, [isEdit, id]);

  useEffect(() => {
    if (jobTargetGroupData) {
      reset(jobTargetGroupData);
    }
  }, [jobTargetGroupData, reset]);

  const onSubmit = async (data: JobTargetGroupState) => {
    try {
      isEdit
        ? await JobTargetGroupsService.update(data, +id!)
        : await JobTargetGroupsService.create(data);

      SuccessToast(
        t(`jobTargetGroup.${isEdit ? 'successUpdate' : 'successCreate'}`)
      );
      navigate('/job-target-groups');
      reset();
    } catch (error) {
      ErrorToast(error);
    }
  };

  return (
    <React.Fragment>
      <Row>
        <Col sm={12}>
          <Card>
            <CardBody>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <FormErrorSummary errors={errors} />
                <Row className="g-3">
                  <Col sm={12} xl={6}>
                    <div className="mb-0">
                      <label className="form-label">{t("jobTargetGroup.title")}</label>
                      <Controller
                        name="title"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                            placeholder={t("jobTargetGroup.enterTitle")}
                            {...field}
                          />
                        )}
                      />
                      {errors.title && <div className="invalid-feedback">
                        {t(errors.title.message as string)}
                      </div>}
                    </div>
                  </Col>
                  <Col sm={12}>
                    <Button type="submit" variant="primary">
                      {t(`jobTargetGroup.${isEdit ? 'update' : 'create'}`)}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default JobTargetGroupsForm;